import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface SiteSettings {
  id: string;
  section: string;
  key: string;
  value: string;
  updated_at: string;
}

export interface SiteConfig {
  id: string;
  site_name: string;
  site_description: string;
  logo_url: string;
  favicon_url: string;
  contact_email: string;
  contact_phone: string;
  social_links: Record<string, string>;
  seo_title: string;
  seo_description: string;
  scripts_header?: string;
  scripts_body_start?: string;
  scripts_footer?: string;
  updated_at: string;
}

export interface StaticContent {
  id: string;
  page: string;
  section: string;
  content: string;
  updated_at: string;
}

export interface Colors {
  primary: string;
  primary_hover: string;
  secondary: string;
  accent: string;
  background: string;
  foreground: string;
  card: string;
  card_hover: string;
  muted: string;
  muted_foreground: string;
  border: string;
  input: string;
  ring: string;
  gradient_primary: string;
  gradient_secondary: string;
  gradient_accent: string;
  gradient_hero: string;
}

export interface Fonts {
  font_family_display: string;
  font_family_body: string;
  font_size_base: number;
  font_size_display_lg: number;
  font_size_display_md: number;
  font_size_display_sm: number;
  line_height_tight: number;
  line_height_normal: number;
  line_height_relaxed: number;
}

export interface Layout {
  container_width: number;
  border_radius: number;
  spacing_unit: number;
  max_width_content: number;
}

// Fetch all settings
export function useSiteSettings() {
  return useQuery({
    queryKey: ['site_settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .order('section', { ascending: true });

      if (error) throw error;
      return data as SiteSettings[];
    },
  });
}

// Fetch settings by section
export function useSettingsBySection(section: string) {
  return useQuery({
    queryKey: ['site_settings', section],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .eq('section', section);

      if (error) throw error;
      return data as SiteSettings[];
    },
  });
}

// Fetch colors
export function useColors() {
  return useQuery({
    queryKey: ['site_settings', 'colors'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .eq('section', 'colors');

      if (error) throw error;

      const colors: any = {};
      data?.forEach((item) => {
        colors[item.key] = item.value;
      });

      return colors as Colors;
    },
  });
}

// Fetch fonts
export function useFonts() {
  return useQuery({
    queryKey: ['site_settings', 'fonts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .eq('section', 'fonts');

      if (error) throw error;

      const fonts: any = {};
      data?.forEach((item) => {
        fonts[item.key] = item.value;
      });

      return fonts as Fonts;
    },
  });
}

// Fetch layout
export function useLayout() {
  return useQuery({
    queryKey: ['site_settings', 'layout'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .eq('section', 'layout');

      if (error) throw error;

      const layout: any = {};
      data?.forEach((item) => {
        layout[item.key] = item.value;
      });

      return layout as Layout;
    },
  });
}

// Fetch site config (most recent record)
export function useSiteConfig() {
  return useQuery({
    queryKey: ['site_config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_config')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as SiteConfig;
    },
  });
}

// Fetch static content by page
export function useStaticContent(page: string) {
  return useQuery({
    queryKey: ['static_content', page],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('static_content')
        .select('*')
        .eq('page', page)
        .order('section', { ascending: true });

      if (error) throw error;
      return data as StaticContent[];
    },
  });
}

// Update setting
export function useUpdateSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ section, key, value }: { section: string; key: string; value: string }) => {
      const { data, error } = await supabase
        .from('site_settings')
        .upsert({ section, key, value })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site_settings'] });
    },
  });
}

// Update multiple settings
export function useUpdateSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: { section: string; key: string; value: string }[]) => {
      const { data, error } = await supabase
        .from('site_settings')
        .upsert(settings)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site_settings'] });
    },
  });
}

// Update site config (always updates the most recent record)
export function useUpdateSiteConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: Partial<SiteConfig>) => {
      // First, get the most recent config to get its ID
      const { data: existing } = await supabase
        .from('site_config')
        .select('id')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existing?.id) {
        // Update existing record
        const { data, error } = await supabase
          .from('site_config')
          .update({ ...config, updated_at: new Date().toISOString() })
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Insert new record if none exists
        const { data, error } = await supabase
          .from('site_config')
          .insert(config)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site_config'] });
    },
  });
}

// Update static content
export function useUpdateStaticContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ page, section, content }: { page: string; section: string; content: string }) => {
      const { data, error } = await supabase
        .from('static_content')
        .upsert({ page, section, content })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['static_content'] });
    },
  });
}

// Get parsed content
export function useParsedStaticContent<T = any>(page: string, section: string) {
  return useQuery({
    queryKey: ['static_content', page, section],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('static_content')
        .select('content')
        .eq('page', page)
        .eq('section', section)
        .single();

      if (error) throw error;

      try {
        return JSON.parse(data?.content || '{}') as T;
      } catch {
        return {} as T;
      }
    },
  });
}
